import { FC } from "react";

import { FormType, getCurrencyFromCountry } from "@simplyk/common";

import { useDonorsListBlockState } from "../../donor-list/useDonorsListBlockState";

import { CampaignBanner } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignBanner/CampaignBanner";
import { CampaignEndedChip } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignEndedChip/CampaignEndedChip";
import { CampaignOrgChip } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignOrgChip/CampaignOrgChip";
import { AnnouncedCampaignTargetThermometer } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignTargetThermometer/AnnouncedCampaignTargetThermometer";
import { PeerToPeerPrimaryCampaignSummaryLayout } from "@/canvas/features/FormV2/blocks/payment/Summary/PeerToPeerPrimaryCampaignSummaryLayout";
import { OrganizationHostedBy } from "@/canvas/features/FormV2/blocks/peer-to-peer/OrganizationHostedBy/OrganizationHostedBy";
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { trpc } from "@/common/helpers/trpc";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormCampaignTargetThermometerSkeleton } from "@/forms-design-system/FormCampaignTargetThermometer";
import { FormDivider } from "@/forms-design-system/FormDivider";

type PeerToPeerPrimaryCampaignSummaryProps = {
  coreInfo?: React.ReactNode;
};

export const PeerToPeerPrimaryCampaignSummary: FC<PeerToPeerPrimaryCampaignSummaryProps> = ({ coreInfo }) => {
  const { formObject, formOrganization, bannerAspectRatio, hasBanner } = useFormV2Context(FormType.DonationForm);

  const shouldDisplayDonorList = formObject.displayDonorList;

  const currency = getCurrencyFromCountry(formOrganization.country);

  const { hasDonations } = useDonorsListBlockState({
    donationFormId: formObject.id,
    enabled: shouldDisplayDonorList,
  });

  const isCampaign = !!formObject.target;
  const isOneColumnLayout = !hasBanner && !hasDonations && !isCampaign;

  const { isSmallScreen } = useMediaQuery();
  const { isPreview } = usePreviewContext();
  const isThermometerGhostPreview = useIsGhostPreview("FormV2Thermometer");

  const { data: campaignVolumeData, isLoading: isCampaignLoading } = trpc.form_getStoredFormVolume.useQuery(
    {
      formId: formObject.id,
      formType: FormType.DonationForm,
    },
    {
      enabled: isCampaign && !isPreview,
    }
  );

  const banner = formObject.bannerUrl
    ? { imageSrc: formObject.bannerUrl, imageAlt: formObject.bannerAltText }
    : formObject.bannerVideoUrl
      ? { videoSrc: formObject.bannerVideoUrl }
      : undefined;

  const campaign = isCampaign
    ? {
        isLoading: isCampaignLoading && !isPreview,
        currentCollectedAmount: isPreview ? (formObject?.target ?? 0) * 0.3 : (campaignVolumeData ?? 0),
        targetedAmount: formObject.target ?? 0,
        currency,
      }
    : undefined;

  return (
    <PeerToPeerPrimaryCampaignSummaryLayout
      slots={{
        banner: banner && <CampaignBanner {...banner} aspectRatio={bannerAspectRatio} />,
        organizationLogo: isSmallScreen ? (
          <CampaignOrgChip />
        ) : (
          <OrganizationHostedBy logoSrc={formObject.logoUrl} organizationName={formObject.organization?.name} />
        ),
        status: formObject.isArchived && <CampaignEndedChip />,
        coreInfo,
        campaignTarget: campaign ? (
          campaign.isLoading ? (
            <FormCampaignTargetThermometerSkeleton />
          ) : (
            <AnnouncedCampaignTargetThermometer
              totalCollectedAmount={campaign.currentCollectedAmount}
              targetedAmount={campaign.targetedAmount}
              currency={campaign.currency}
              sx={{
                opacity: isThermometerGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
              }}
            />
          )
        ) : undefined,
        divider: !isOneColumnLayout ? <FormDivider sx={{ width: "100%" }} /> : undefined,
      }}
    />
  );
};
